/**
 * Small cross-page form/flash niceties:
 *
 * 1. No-reload toggle controls: a <form data-toggle-fetch> submits through
 *    fetch() with Accept: application/json instead of navigating, so a
 *    publish/active toggle never blurs focus or scrolls the page back to the
 *    top (UX item 7 — 포커스 튐). The handler behind the same action/route
 *    answers a normal 303 redirect for a plain browser submit and only
 *    switches to JSON when it sees that header, so the control keeps working
 *    with JS disabled. data-toggle-row names a CSS selector (relative to the
 *    form, via closest()) for the row/container whose .badge should update;
 *    it defaults to the form's parent element. data-toggle-error is the
 *    localized message for the toast shown on failure. The JSON body (every
 *    field optional):
 *      { ok, badge: {class, text},
 *        button: {text, title, pressed, icon, confirm, confirmDanger},
 *        buttons: [{value, active}] }
 *    `button` updates the clicked submit button (text/title/aria-pressed,
 *    and an icon swap between sibling .tgl-icon-<name> spans); `buttons`
 *    instead updates every button in a segmented control (module publish)
 *    matched by its value attribute.
 *    (모듈 공개 토글이 사이드바를 제자리에서 갈아끼우던 data-toggle-nav 경로는
 *    §3.7.3 에서 사라졌다: 그 토글이 사는 /admin/config 는 전용 관리자
 *    사이드바를 그리므로 갈아끼울 <nav data-global-nav> 가 그 문서에 없다.
 *    새 모듈의 그룹은 다음 전역 화면에서 서버 렌더로 나타난다.)
 * 2. Double-submit guard: once a POST form actually submits (i.e. was not
 *    intercepted by the confirm dialog), its submit buttons disable so a slow
 *    network cannot produce two documents/decisions. Runs in the bubble phase
 *    AFTER the capture-phase confirm interceptor, so a cancelled confirm never
 *    disables anything.
 * 3. Success flashes auto-dismiss after a few seconds — on first load and on
 *    every in-place swap (calendar:navigated / calendar:page-swapped), since a
 *    re-rendered page brings a brand new flash node (E9). A flash marked
 *    data-persistent (or carrying the alert-persistent class) stays until the
 *    state it describes changes — e.g. "TOTP already enabled" (UX item 9).
 */
function applyToggleResponse(form, submitter, data) {
    const rowSelector = form.dataset.toggleRow;
    const row = (rowSelector && form.closest(rowSelector)) || form.parentElement || form;

    if (data.badge) {
        const badge = row.querySelector('.badge');
        if (badge) {
            badge.className = 'badge ' + data.badge.class;
            badge.textContent = data.badge.text;
        }
    }

    if (Array.isArray(data.buttons)) {
        data.buttons.forEach((b) => {
            const btn = form.querySelector('button[value="' + CSS.escape(String(b.value)) + '"]');
            if (!btn) return;
            btn.classList.toggle('active', !!b.active);
            btn.disabled = !!b.active;
            btn.setAttribute('aria-pressed', b.active ? 'true' : 'false');
        });
        return;
    }

    if (data.button && submitter) {
        const btn = data.button;
        if (typeof btn.text === 'string') submitter.textContent = btn.text;
        if (typeof btn.title === 'string') submitter.setAttribute('title', btn.title);
        if (btn.pressed !== undefined) submitter.setAttribute('aria-pressed', btn.pressed ? 'true' : 'false');
        if (btn.icon) {
            submitter.querySelectorAll('[class^="tgl-icon-"]').forEach((el) => {
                el.hidden = el.className !== 'tgl-icon-' + btn.icon;
            });
        }
        if (btn.confirm) {
            form.dataset.confirm = btn.confirm;
            if (btn.confirmDanger) form.dataset.confirmDanger = '';
            else delete form.dataset.confirmDanger;
        } else {
            delete form.dataset.confirm;
            delete form.dataset.confirmDanger;
        }
    }
}

function handleToggleFetch(form, submitter) {
    if (form.dataset.togglePending === '1') return;
    form.dataset.togglePending = '1';

    const url = form.getAttribute('action');
    const fd = submitter ? new FormData(form, submitter) : new FormData(form);

    fetch(url, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: fd })
        .then((r) => r.json().catch(() => null).then((data) => {
            if (!r.ok || !data || data.ok !== true) throw new Error((data && data.error) || 'http_error');
            return data;
        }))
        .then((data) => {
            applyToggleResponse(form, submitter, data);
            // 토글 하나가 화면의 다른 곳(헤더 모듈 탭 …)도 바꿀 수 있다.
            // 무엇을 다시 그릴지는 그 화면의 모듈이 정한다.
            form.dispatchEvent(new CustomEvent('form-ux:toggled', { bubbles: true, detail: data }));
        })
        .catch(() => {
            if (window.showNotification) window.showNotification(form.dataset.toggleError || 'Failed to update', 'error');
        })
        .finally(() => { delete form.dataset.togglePending; });
}

export function initFormUX() {
    document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement) || e.defaultPrevented) return;
        if ((form.method || '').toLowerCase() !== 'post') return;

        if (form.hasAttribute('data-toggle-fetch') && window.fetch && window.FormData) {
            e.preventDefault();
            handleToggleFetch(form, e.submitter || null);
            return;
        }

        // Delay past this tick so the browser serializes the still-enabled
        // buttons (a disabled submitter's name/value would be dropped).
        setTimeout(() => {
            form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((b) => {
                b.disabled = true;
                b.classList.add('is-submitting');
            });
        }, 0);
    });

    armFlashDismiss();
    // 제자리 교체(관리자 SPA·calendar-spa) 뒤에 도착한 flash 도 사라져야 한다 —
    // 예전에는 최초 로드에만 타이머를 걸어 저장할 때마다 성공 문구가 쌓였다 (E9).
    // data-flash-armed 표식 덕분에 여러 번 불려도 타이머는 하나다.
    document.addEventListener('calendar:navigated', armFlashDismiss);
    document.addEventListener('calendar:page-swapped', armFlashDismiss);
}

// armFlashDismiss starts the fade timer on every success flash that has none.
function armFlashDismiss() {
    document.querySelectorAll('.alert-success').forEach((el) => {
        if (el.hasAttribute('data-persistent') || el.classList.contains('alert-persistent')) return;
        if (el.dataset.flashArmed === '1') return;
        el.dataset.flashArmed = '1';
        setTimeout(() => {
            el.style.transition = 'opacity 300ms, margin 300ms, max-height 300ms';
            el.style.opacity = '0';
            el.style.maxHeight = el.offsetHeight + 'px';
            requestAnimationFrame(() => {
                el.style.maxHeight = '0';
                el.style.marginBottom = '0';
                el.style.overflow = 'hidden';
            });
            setTimeout(() => el.remove(), 350);
        }, 4000);
    });
}
